/**
 * Vanturis Navbar — Iteration 81
 *
 * Role-aware header:
 *
 *  • ANON (public)        Marketplace ▾ · Sell · Trust · Concierge · More ▾ | Sign In · List
 *  • SELLER / CLIENT      Dashboard · My Listings · Marketplace ▾ · Help    | Account · Sign Out
 *  • DEALER               Dashboard · Marketplace ▾ · Help                   | Account · Sign Out
 *  • PARTNER              Dashboard · Help                                    | Account · Sign Out
 *  • INSPECTOR            Inspections · Help                                  | Account · Sign Out
 *  • INTERNAL / ADMIN     Admin · Marketplace ▾ · Concierge (if concierge staff) | Account · Sign Out
 *
 * Mobile: single hamburger button + full-viewport drawer with role-scoped
 * items. Larger logo, tighter vertical padding — header height unchanged.
 *
 * The `TEST` role-switcher is Preview-only and hidden in Production.
 */
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";
import { useLaunch } from "@/context/LaunchContext";
import { isInternal, canListCoach, roleLabel } from "@/lib/roles";
import RoleSwitcher from "@/components/RoleSwitcher";
import Logo from "@/components/Logo";
import { COLLECTIONS } from "@/lib/collections";
import { Menu, X, ChevronDown } from "lucide-react";
import { useDataMgmtFlags } from "@/lib/useDataMgmtFlags";

const link = ({ isActive }) =>
  `text-[11px] tracking-[0.22em] uppercase transition-colors ${
    isActive ? "text-copper" : "text-ink-50 hover:text-copper"
  }`;

const mobileLink = ({ isActive }) =>
  `block py-4 border-b border-ivory-400 text-base tracking-[0.14em] uppercase ${
    isActive ? "text-copper" : "text-ink"
  }`;

const mobileSectionLabel =
  "px-0 pt-4 pb-2 text-[10px] uppercase tracking-[0.28em] text-ink-100";

/* ------------------------------------------------------------------ */
/*  Small mega-menu for the "Marketplace" pill                         */
/* ------------------------------------------------------------------ */
function MarketplaceMega({ open, setOpen }) {
  // Grace-period close on hover-out so the cursor can travel from the
  // trigger down into the dropdown without the menu disappearing.  Also
  // handles Escape-to-close for keyboard accessibility.
  const closeTimer = useRef(null);
  const openNow = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };
  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);
  return (
    <div
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      data-testid="nav-marketplace-wrap"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        onFocus={openNow}
        className={`flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] transition hover:text-copper ${
          open ? "text-copper" : "text-ink-50"
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid="nav-marketplace"
      >
        Marketplace <ChevronDown className="w-3 h-3" strokeWidth={2} />
      </button>
      {open && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[min(920px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)]"
          data-testid="nav-mega"
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
        >
          <div className="bg-ivory border border-ivory-400 shadow-xl grid grid-cols-3 gap-8 p-8">
            {[
              {
                title: "Land — Vanturis Specialty",
                slugs: ["ultra-luxury-motorcoaches", "touring-collection",
                        "premium-class-a", "premium-super-c"],
              },
              {
                title: "Land — Overland & Trailers",
                slugs: ["overland-vehicles", "overland-trailers", "specialty-trailers", "european-collection"],
              },
              {
                title: "Air & Water — Trusted Partners",
                note: "Curated specialist network",
                slugs: ["private-aviation", "luxury-marine", "auctions"],
              },
            ].map((col) => (
              <div key={col.title}>
                <div className="overline mb-4">{col.title}</div>
                {col.note && (
                  <div className="text-[10px] text-ink-100 -mt-3 mb-4 normal-case tracking-normal italic">
                    {col.note}
                  </div>
                )}
                <ul className="space-y-3">
                  {col.slugs.map((slug) => {
                    const c = COLLECTIONS.find((x) => x.slug === slug);
                    if (!c) return null;
                    const to = ["private-aviation", "luxury-marine"].includes(c.slug)
                      ? c.preview_route
                      : `/browse?collection=${c.slug}`;
                    return (
                      <li key={c.slug}>
                        <Link
                          to={to}
                          onClick={() => setOpen(false)}
                          className="block group"
                          data-testid={`nav-mega-${c.slug}`}
                        >
                          <div className="font-serif text-lg text-ink group-hover:text-copper leading-tight tracking-tight">
                            {c.name}
                          </div>
                          <div className="text-[11px] text-ink-100 mt-0.5 normal-case tracking-normal">
                            {c.tagline}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <div className="bg-ivory-100 border-x border-b border-ivory-400 flex items-center justify-between px-8 py-3 text-xs">
            <Link to="/browse" onClick={() => setOpen(false)}
                  className="text-ink hover:text-copper uppercase tracking-[0.22em]"
                  data-testid="nav-mega-browse-all">
              Browse All Listings
            </Link>
            <Link to="/collections" onClick={() => setOpen(false)}
                  className="text-copper hover:underline uppercase tracking-[0.22em]"
                  data-testid="nav-mega-collections-index">
              Explore Collections
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  "Sell" pill – primary seller nav with "Why Vanturis" sub-link      */
/* ------------------------------------------------------------------ */
function SellMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="nav-sell-wrap"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] transition hover:text-copper ${
          open ? "text-copper" : "text-ink-50"
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid="nav-sell"
      >
        Sell <ChevronDown className="w-3 h-3" strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 min-w-[220px]"
             data-testid="nav-sell-menu">
          <div className="bg-ivory border border-ivory-400 shadow-lg p-3 space-y-1">
            <NavLink to="/sell"
                     className="block py-2 px-3 text-[11px] tracking-[0.22em] uppercase text-ink-50 hover:text-copper"
                     data-testid="nav-sell-overview"
                     onClick={() => setOpen(false)}>Sell Overview</NavLink>
            <NavLink to="/why-list-with-vanturis"
                     className="block py-2 px-3 text-[11px] tracking-[0.22em] uppercase text-ink-50 hover:text-copper"
                     data-testid="nav-sell-why-vanturis"
                     onClick={() => setOpen(false)}>Why Vanturis</NavLink>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  "More" pill – secondary desktop links (Partners, Pricing)          */
/* ------------------------------------------------------------------ */
function MoreMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="nav-more-wrap"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] transition hover:text-copper ${
          open ? "text-copper" : "text-ink-50"
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid="nav-more"
      >
        More <ChevronDown className="w-3 h-3" strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute top-full right-0 pt-3 min-w-[180px]"
             data-testid="nav-more-menu">
          <div className="bg-ivory border border-ivory-400 shadow-lg p-3 space-y-1">
            <NavLink to="/partners"
                     className="block py-2 px-3 text-[11px] tracking-[0.22em] uppercase text-ink-50 hover:text-copper"
                     data-testid="nav-more-partners">Partners</NavLink>
            <NavLink to="/become-a-partner"
                     className="block py-2 px-3 text-[11px] tracking-[0.22em] uppercase text-copper hover:text-bronze"
                     data-testid="nav-more-become-a-partner">Become a Partner</NavLink>
            <NavLink to="/pricing"
                     className="block py-2 px-3 text-[11px] tracking-[0.22em] uppercase text-ink-50 hover:text-copper"
                     data-testid="nav-more-pricing">Pricing</NavLink>
            <NavLink to="/collections"
                     className="block py-2 px-3 text-[11px] tracking-[0.22em] uppercase text-ink-50 hover:text-copper"
                     data-testid="nav-more-collections">Collections</NavLink>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
export default function Navbar() {
  const { user, logout } = useAuth();
  const { config: launch } = useLaunch();
  const { environment } = useDataMgmtFlags();
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const nav = useNavigate();

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (open) {
      const b = document.body.style.overflow;
      const h = document.documentElement.style.overflow;
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = b;
        document.documentElement.style.overflow = h;
      };
    }
  }, [open]);

  const closeMenu = () => setOpen(false);
  const logoutAndGo = async () => {
    closeMenu();
    await logout();
    nav("/");
  };

  const isPreview = environment === "preview";
  const isSeller = !!user && canListCoach(user);
  const isAdmin = !!user && isInternal(user);
  const isInspector = !!user && user.role === "inspector";
  const isPartner = !!user && user.role === "partner";

  /* Determine which role-scoped desktop items to render (kept to at most 6).
     Passport is a PRIMARY product-level destination (owner directive) — it
     appears top-level for every role and is NEVER gated by PASSPORT_ENABLED
     (it links to the public product/marketing entry, not private data). */
  const passportItem = { to: "/passport", label: "Passport", testid: "nav-passport" };
  const desktopItems = (() => {
    if (isAdmin) return [
      { to: "/admin", label: "Admin", testid: "nav-admin" },
      { mega: true },
      passportItem,
      { to: "/concierge", label: "Concierge", testid: "nav-concierge" },
    ];
    if (isInspector) return [
      { to: "/inspector", label: "Inspections", testid: "nav-inspector" },
      passportItem,
      { to: "/concierge", label: "Help", testid: "nav-help" },
    ];
    if (isSeller) return [
      { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard-link" },
      { to: "/workspace", label: "My Listings", testid: "nav-workspace" },
      { mega: true },
      passportItem,
      { to: "/concierge", label: "Help", testid: "nav-help" },
    ];
    if (isPartner) return [
      { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard-link" },
      passportItem,
      { to: "/concierge", label: "Help", testid: "nav-help" },
    ];
    // Anonymous / public
    return [
      { mega: true },
      passportItem,
      { sell: true },
      { to: "/trust", label: "Trust", testid: "nav-trust" },
      { to: "/concierge", label: "Concierge", testid: "nav-concierge" },
      { more: true },
    ];
  })();

  return (
    <header
      className="sticky top-0 z-40 bg-ivory/90 backdrop-blur-xl border-b border-ivory-400"
      data-testid="site-header"
    >
      <div className="container-editorial flex items-center justify-between py-3 md:py-3.5 gap-4">
        {/* ── Logo (larger, less padding) ───────────────────────────── */}
        <Link
          to="/"
          className="flex items-center gap-3 group flex-shrink-0"
          data-testid="nav-logo"
          aria-label="Vanturis home"
        >
          <Logo
            variant="master-black"
            height={52}
            className="!h-[58px] sm:!h-[64px] md:!h-[72px] lg:!h-[76px] scale-[1.15] origin-left"
          />
        </Link>

        {/* ── Desktop nav ──────────────────────────────────────────── */}
        <nav
          className="hidden lg:flex items-center gap-8 flex-1 justify-center"
          aria-label="Primary"
        >
          {desktopItems.map((it, i) => {
            if (it.mega) return (
              <MarketplaceMega key={`mega-${i}`} open={megaOpen} setOpen={setMegaOpen} />
            );
            if (it.sell) return <SellMenu key={`sell-${i}`} />;
            if (it.more) return <MoreMenu key={`more-${i}`} />;
            return (
              <NavLink key={it.testid} to={it.to} className={link}
                       data-testid={it.testid}>{it.label}</NavLink>
            );
          })}
        </nav>

        {/* ── Right-side actions ──────────────────────────────────── */}
        <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
          {user ? (
            <>
              {isPreview && (
                <div className="pr-2 border-r border-ivory-400" data-testid="nav-preview-role-switcher">
                  <RoleSwitcher />
                </div>
              )}
              <Link
                to="/account/security"
                className="text-[11px] uppercase tracking-[0.22em] text-ink-50 hover:text-copper"
                data-testid="nav-account-security"
              >
                Account
              </Link>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="btn-secondary !py-2 !px-4 text-[11px]"
                data-testid="nav-logout"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              {/*  Launch-mode-aware primary CTA:
                   coming_soon  → JOIN EARLY ACCESS  (routes to /early-access)
                   public_launch or private_preview → LIST YOUR COACH (routes to /list-coach)
              */}
              {launch?.launch_mode === "coming_soon" ? (
                <Link to="/early-access" className="btn-primary !py-2 !px-4 text-[11px]"
                      data-testid="nav-cta-early-access">
                  Join Early Access
                </Link>
              ) : (
                <>
                  <Link to="/login" className="btn-ghost !py-2 !px-4 text-[11px]" data-testid="nav-signin">Sign In</Link>
                  <Link to="/list-coach" className="btn-primary !py-2 !px-4 text-[11px]"
                        data-testid="nav-list-coach">
                    List Your Coach
                  </Link>
                </>
              )}
            </>
          )}
        </div>

        {/* ── Mobile hamburger only ──────────────────────────────── */}
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-ink -mr-2"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* ── Mobile drawer (portal, role-scoped) ─────────────────── */}
      {open && createPortal(
        <div
          className="lg:hidden fixed inset-0 z-[100] overflow-hidden bg-ivory"
          data-testid="nav-mobile-menu"
          role="dialog"
          aria-modal="true"
        >
          <div className="h-full flex flex-col bg-ivory overflow-y-auto">
            <div className="container-editorial flex items-center justify-between py-3 border-b border-ivory-400 bg-ivory">
              <Link to="/" onClick={closeMenu} className="flex items-center gap-3">
                <Logo variant="master-black" height={44} />
              </Link>
              <button
                onClick={closeMenu}
                aria-label="Close menu"
                className="text-ink p-2 -mr-2"
                data-testid="nav-mobile-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <nav
              className="container-editorial flex-1 py-2 flex flex-col bg-ivory"
              aria-label="Mobile primary"
            >
              {user ? (
                <>
                  {/* Primary role actions */}
                  {isAdmin && (
                    <NavLink to="/admin" className={mobileLink} onClick={closeMenu}
                             data-testid="nav-mobile-admin">Admin</NavLink>
                  )}
                  {isInspector && (
                    <NavLink to="/inspector" className={mobileLink} onClick={closeMenu}
                             data-testid="nav-mobile-inspector">Inspections</NavLink>
                  )}
                  {(isSeller || isPartner) && (
                    <NavLink to="/dashboard" className={mobileLink} onClick={closeMenu}
                             data-testid="nav-mobile-dashboard">Dashboard</NavLink>
                  )}
                  {isSeller && (
                    <NavLink to="/workspace" className={mobileLink} onClick={closeMenu}
                             data-testid="nav-mobile-workspace">My Listings</NavLink>
                  )}

                  {/* Products (primary for auth users) */}
                  <div className={mobileSectionLabel}>Explore</div>
                  <NavLink to="/browse" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-browse">Marketplace</NavLink>
                  <NavLink to="/passport" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-passport">Passport</NavLink>
                  <NavLink to="/concierge" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-concierge">Help &amp; Concierge</NavLink>

                  {/* Account */}
                  <div className={mobileSectionLabel}>Account</div>
                  <NavLink to="/account/security" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-account-security">Account &amp; Security</NavLink>

                  <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-ink-100 pb-2">
                    Signed in as{" "}
                    <span className="text-ink">
                      {user.name?.split(" ")[0] || user.email}
                    </span>{" "}
                    · {roleLabel(user.role)}
                  </div>
                  <button
                    onClick={logoutAndGo}
                    className="btn-secondary mt-2 w-full text-center py-3"
                    data-testid="nav-mobile-logout"
                  >
                    Sign Out
                  </button>
                  {isPreview && (
                    <div className="mt-6 pt-4 border-t border-ivory-400"
                         data-testid="nav-mobile-preview-role-switcher">
                      <div className={mobileSectionLabel}>Preview only</div>
                      <RoleSwitcher />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <NavLink to="/browse" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-browse">Marketplace</NavLink>
                  <NavLink to="/passport" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-passport">Passport</NavLink>
                  <NavLink to="/sell" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-sell">Sell</NavLink>
                  <NavLink to="/why-list-with-vanturis" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-why-vanturis">Why Vanturis</NavLink>
                  <NavLink to="/trust" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-trust">Trust</NavLink>
                  <NavLink to="/concierge" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-concierge">Concierge</NavLink>

                  <div className={mobileSectionLabel}>Air &amp; Water · Trusted Partners</div>
                  <NavLink to="/marketplace/private-aviation" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-private-aviation">Private Aviation</NavLink>
                  <NavLink to="/marketplace/luxury-marine" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-luxury-marine">Luxury Marine</NavLink>

                  <div className={mobileSectionLabel}>More</div>
                  <NavLink to="/partners" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-partners">Partners</NavLink>
                  <NavLink to="/become-a-partner" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-become-a-partner">Become a Partner</NavLink>
                  <NavLink to="/pricing" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-pricing">Pricing</NavLink>
                  <NavLink to="/collections" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-collections">Collections</NavLink>

                  <NavLink to="/login" className={mobileLink} onClick={closeMenu}
                           data-testid="nav-mobile-signin">Sign In</NavLink>
                  {launch?.launch_mode === "coming_soon" ? (
                    <Link to="/early-access" onClick={closeMenu}
                          className="btn-primary mt-6 w-full text-center py-3"
                          data-testid="nav-mobile-early-access">
                      Join Early Access
                    </Link>
                  ) : (
                    <Link to="/list-coach" onClick={closeMenu}
                          className="btn-primary mt-6 w-full text-center py-3"
                          data-testid="nav-mobile-list-coach">
                      List Your Coach
                    </Link>
                  )}
                </>
              )}
            </nav>
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}
